import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  ProgressBar,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaCalendar,
  FaStickyNote,
  FaPalette,
  FaChartLine,
  FaSync,
  FaBuilding,
  FaTools,
  FaFileAlt,
  FaCalendarCheck,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarDay,
  FaRegCalendarAlt,
  FaExclamationCircle,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const ActionBidSchedule = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [actionSchedule, setActionSchedule] = useState(null);
  const [apiError, setApiError] = useState(null);
  // Removed bidTaskInfo state since we can't fetch it
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  /* ================= FETCH ACTION SCHEDULE ================= */
  const fetchActionSchedule = async () => {
    try {
      setLoading(true);
      setApiError(null);
      setActionSchedule(null);
      
      // Try to fetch action schedule
      const res = await axios.get(
        `${API_BASE}/data/getActionScheduleByBidTask/${taskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setActionSchedule(res.data.actionSchedule);
      } else {
        // Handle case where API returns success: false
        setApiError(res.data.message || 'No action schedule found');
        toast.info(res.data.message || 'No action schedule found');
      }
    } catch (error) {
      console.error('Fetch action schedule error:', error);
      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
        toast.info(error.response.data.message);
      } else {
        setApiError('Action schedule not found for this bid task');
        toast.info('Action schedule not found for this bid task');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchActionSchedule();
    }
  }, [taskId]);

  /* ================= FORMATTERS ================= */
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const parseEstimatedTime = (timeString) => {
    if (!timeString) return { value: 0, unit: 'days' };
    
    const match = timeString.match(/(\d+)\s*(\w+)/);
    if (match) {
      return {
        value: parseInt(match[1]),
        unit: match[2].toLowerCase()
      };
    }
    return { value: 0, unit: 'days' };
  };

  const getColorBadge = (color) => {
    const colorMap = {
      'green': { bg: 'success', text: 'Green' },
      'red': { bg: 'danger', text: 'Red' },
      'yellow': { bg: 'warning', text: 'Yellow' },
      'blue': { bg: 'info', text: 'Blue' },
      'orange': { bg: 'warning', text: 'Orange' },
      'purple': { bg: 'primary', text: 'Purple' },
    };
    
    const colorInfo = colorMap[color] || { bg: 'secondary', text: color || 'Not set' };
    
    return (
      <Badge bg={colorInfo.bg} className="px-3 py-2">
        <FaPalette className="me-2" />
        {colorInfo.text}
      </Badge>
    );
  };

  const getTimeProgress = (timeString) => {
    const time = parseEstimatedTime(timeString);
    let progress = 0;
    
    if (time.unit.includes('day')) {
      progress = Math.min((time.value / 100) * 100, 100);
    } else if (time.unit.includes('week')) {
      progress = Math.min((time.value / 12) * 100, 100);
    } else if (time.unit.includes('month')) {
      progress = Math.min((time.value / 3) * 100, 100);
    } else {
      progress = 50; // Default progress for unknown units
    }
    
    return Math.round(progress);
  };

  const getProgressVariant = (progress) => {
    if (progress >= 80) return 'success';
    if (progress >= 50) return 'info';
    if (progress >= 30) return 'warning';
    return 'danger';
  };

  const getTimeIcon = (timeString) => {
    const time = parseEstimatedTime(timeString);
    if (time.value <= 7) return <FaExclamationTriangle className="text-danger" />;
    if (time.value <= 30) return <FaClock className="text-warning" />;
    return <FaCalendarCheck className="text-success" />;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading action schedule...</span>
      </div>
    );
  }

  const timeProgress = actionSchedule ? getTimeProgress(actionSchedule.estimatedTime) : 0;
  const progressVariant = getProgressVariant(timeProgress);
  const timeData = actionSchedule ? parseEstimatedTime(actionSchedule.estimatedTime) : { value: 0, unit: 'days' };

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Bid Estimates
              </Button>
              <h2 className="fw-bold mb-2">
                <FaCalendarAlt className="me-3" />
                Action Schedule
              </h2>
              
              {/* Simplified header without bidTaskInfo */}
              <div className="mb-3">
                <h4 className="mb-1">
                  <FaFileAlt className="me-2 text-primary" />
                  Task ID: {taskId?.substring(0, 16)}...
                </h4>
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <Badge bg="info" className="px-3 py-2">
                    <FaCalendarAlt className="me-2" />
                    Action Schedule
                  </Badge>
                  <Badge bg="secondary" className="px-3 py-2">
                    <FaFileAlt className="me-2" />
                    Task ID: {taskId?.substring(0, 12)}...
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={fetchActionSchedule}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* API ERROR MESSAGE - Show only when there's an error and no action schedule */}
      {apiError && !actionSchedule && (
        <Alert variant="warning" className="mb-4">
          <div className="d-flex align-items-center">
            <FaExclamationCircle className="me-3" size={24} />
            <div>
              <h5 className="alert-heading mb-2">Action Schedule Information</h5>
              <p className="mb-2">{apiError}</p>
              <hr />
              <p className="mb-0 small">
                An action schedule helps track task timelines, priorities, and progress.
                You may need to create one for this bid task.
              </p>
            </div>
          </div>
        </Alert>
      )}

      {/* MAIN CONTENT - Only show if actionSchedule exists */}
      {!actionSchedule ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaCalendarAlt size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Action Schedule Found</h4>
            <p className="text-muted mb-4">
              {apiError || 'No action schedule has been created for this bid task yet.'}
            </p>
            <div className="d-flex justify-content-center gap-3">
              <Button
                variant="outline-primary"
                onClick={fetchActionSchedule}
              >
                <FaSync className="me-2" />
                Check Again
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft className="me-2" />
                Return to Previous Page
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* ACTION SCHEDULE DETAILS */}
          <Row className="g-4 mb-4">
            {/* Left Column - Schedule Details */}
            <Col lg={8}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">
                    <FaRegCalendarAlt className="me-2" />
                    Schedule Details
                  </h4>
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <FaFileAlt className="me-1" />
                    ID: {actionSchedule._id?.substring(0, 8)}...
                  </Badge>
                </Card.Header>
                <Card.Body>
                  {/* Estimated Time Section */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">
                        <FaClock className="me-2" />
                        Estimated Time
                      </h5>
                      {getTimeIcon(actionSchedule.estimatedTime)}
                    </div>
                    
                    <div className="text-center mb-4">
                      <div className="display-4 fw-bold mb-2">
                        {timeData.value}
                        <span className="fs-6 text-muted ms-2">{timeData.unit}</span>
                      </div>
                      <div className="small text-muted">
                        Total Estimated Duration
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Time Progress</small>
                        <small className="text-muted">{timeProgress}%</small>
                      </div>
                      <ProgressBar 
                        now={timeProgress} 
                        variant={progressVariant}
                        style={{ height: '10px' }}
                      />
                    </div>

                    <div className="row text-center mt-4">
                      <Col md={4} className="mb-3">
                        <Card className="border-0 bg-primary bg-opacity-10 h-100">
                          <Card.Body>
                            <div className="fw-bold fs-3">{timeData.value}</div>
                            <div className="text-muted">{timeData.unit}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4} className="mb-3">
                        <Card className={`border-0 bg-${progressVariant} bg-opacity-10 h-100`}>
                          <Card.Body>
                            <div className={`fw-bold fs-3 text-${progressVariant}`}>{timeProgress}%</div>
                            <div className="text-muted">Progress</div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4} className="mb-3">
                        <Card className="border-0 bg-light h-100">
                          <Card.Body>
                            <div className="fw-bold fs-3">
                              {getColorBadge(actionSchedule.color)}
                            </div>
                            <div className="text-muted">Priority Color</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {actionSchedule.notes && (
                    <div className="mb-4">
                      <h5 className="mb-3">
                        <FaStickyNote className="me-2" />
                        Notes
                      </h5>
                      <Card className="border">
                        <Card.Body className="bg-light">
                          <p className="mb-0">{actionSchedule.notes}</p>
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column - Timeline & Info */}
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-light">
                  <h4 className="mb-0">
                    <FaCalendarDay className="me-2" />
                    Timeline Information
                  </h4>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="border-0 px-0 py-3">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                            <FaCalendarAlt className="text-primary" />
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Created On</h6>
                          <div className="small text-muted">
                            {formatDate(actionSchedule.createdAt)}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>

                    <ListGroup.Item className="border-0 px-0 py-3">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="bg-success bg-opacity-10 rounded-circle p-2">
                            <FaCalendarCheck className="text-success" />
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Last Updated</h6>
                          <div className="small text-muted">
                            {formatDate(actionSchedule.updatedAt)}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>

                    <ListGroup.Item className="border-0 px-0 py-3">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="bg-warning bg-opacity-10 rounded-circle p-2">
                            <FaClock className="text-warning" />
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Estimated Duration</h6>
                          <div className="fw-bold">
                            {actionSchedule.estimatedTime || 'Not specified'}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>

                    <ListGroup.Item className="border-0 px-0 py-3">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="bg-info bg-opacity-10 rounded-circle p-2">
                            <FaPalette className="text-info" />
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Priority Color</h6>
                          <div className="d-flex align-items-center">
                            <div 
                              className="rounded-circle me-2" 
                              style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: actionSchedule.color || '#6c757d',
                                border: '2px solid #dee2e6'
                              }}
                            />
                            <span className="text-capitalize">
                              {actionSchedule.color || 'Not set'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* CREATED BY INFORMATION */}
          <Row className="g-4 mb-4">
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-light">
                  <h4 className="mb-0">
                    <FaUser className="me-2" />
                    Created Information
                  </h4>
                </Card.Header>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={4} className="text-center mb-4 mb-md-0">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                        <FaUser size={48} className="text-primary" />
                      </div>
                      <h4 className="mb-1">{actionSchedule.createdBy?.name || 'Unknown'}</h4>
                      <p className="text-muted mb-3">{actionSchedule.createdBy?.email || 'No email provided'}</p>
                      <Badge bg="primary" className="px-3 py-2">
                        <FaUser className="me-2" />
                        Schedule Creator
                      </Badge>
                    </Col>
                    
                    <Col md={8}>
                      <div className="bg-light p-4 rounded">
                        <h5 className="mb-3">Activity Summary</h5>
                        <ListGroup variant="flush">
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Action Schedule Created:</span>
                              <span className="fw-medium">{formatDate(actionSchedule.createdAt)}</span>
                            </div>
                          </ListGroup.Item>
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Last Updated:</span>
                              <span className="fw-medium">{formatDate(actionSchedule.updatedAt)}</span>
                            </div>
                          </ListGroup.Item>
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Status:</span>
                              <Badge bg={timeProgress >= 80 ? 'success' : timeProgress >= 50 ? 'info' : 'warning'}>
                                {timeProgress >= 80 ? 'On Track' : timeProgress >= 50 ? 'In Progress' : 'Planning Phase'}
                              </Badge>
                            </div>
                          </ListGroup.Item>
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Schedule ID:</span>
                              <span className="small text-muted">{actionSchedule._id}</span>
                            </div>
                          </ListGroup.Item>
                        </ListGroup>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* EMPTY STATE INFO - Show only when no action schedule exists */}
      {!actionSchedule && (
        <Card className="border-0 shadow-sm mt-4">
          <Card.Body>
            <Alert variant="info" className="mb-0">
              <div className="d-flex align-items-center">
                <FaInfoCircle className="me-3" size={24} />
                <div>
                  <h5 className="alert-heading mb-2">Action Schedule Management</h5>
                  <p className="mb-0">
                    This page displays the action schedule for the selected bid task.
                    Action schedules include estimated timelines, priority colors, and notes
                    to help track project progress and manage task completion.
                  </p>
                </div>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default ActionBidSchedule;